import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import {
  getReview,
  answerItem,
  addItems,
  undoItem,
  patchExpense,
  uploadExpenseFile,
} from "../../api/expenses";
import { formatMoney } from "../../utils/money";
import { categoryIcon } from "../../utils/expenseCategories";
import { shrinkImage } from "../../utils/files";

// Everything that needs you, in one list:
//   1. questions the AI couldn't answer about statement lines,
//   2. lines it's sure about, ready to add in one tap,
//   3. saved expenses missing a date, card, category or receipt,
//   4. a log of what was added, skipped or matched (with undo).

const PERSONAL = "Personal (not business)";
const CUSTOMER = "Customer payment";
const OTHER_IN = "Loan or other";

const day = (s) => {
  if (!s) return "No date";
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString([], { month: "short", day: "numeric" });
};
const FIXES_SHOWN = 5;
const money = (c) => (Number(c) < 0 ? `+${formatMoney(-Number(c))}` : formatMoney(c));

export default function ReviewPanel({ data, onChanged, onOpenExpense }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [allFixes, setAllFixes] = useState(false);

  const load = useCallback(async () => {
    try {
      setReview(await getReview());
    } catch (e) {
      setError(e.message || "Couldn't load the review list.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, data]);

  const after = (r) => {
    if (r?.review) setReview(r.review);
    else load();
    onChanged();
  };

  async function undo(id) {
    try {
      after(await undoItem(id));
    } catch (e) {
      setError(e.message || "Couldn't undo that.");
    }
  }

  if (!review) {
    return error ? (
      <p className="text-sm text-red-700">{error}</p>
    ) : (
      <div className="py-10 flex justify-center">
        <Spinner label="Loading..." />
      </div>
    );
  }

  const nothing = !review.questions.length && !review.ready.length && !review.fixes.length;

  return (
    <div className="space-y-4">
      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {nothing && (
        <div className="bg-white border border-birdie-green/15 rounded-2xl p-8 text-center">
          <div className="text-4xl">✅</div>
          <h3 className="font-accent text-lg text-birdie-charcoal mt-2">All caught up</h3>
          <p className="text-sm text-birdie-charcoal/60 mt-1">Nothing needs your input. Add statements any time to bring new spending in.</p>
        </div>
      )}

      {review.questions.length > 0 && (
        <Section title="Questions" count={review.questions.length} sub="Tap an answer. Each one adds or skips the line.">
          <div className="space-y-2.5">
            {review.questions.map((q) => (
              <QuestionCard key={q.id} item={q} data={data} onDone={after} onError={setError} />
            ))}
          </div>
        </Section>
      )}

      {review.ready.length > 0 && (
        <ReadyList items={review.ready} data={data} onDone={after} onError={setError} />
      )}

      {review.fixes.length > 0 && (
        <Section title="Needs updating" count={review.fixes.length} sub="Saved expenses that are missing something. Fix them right here.">
          <ul className="space-y-2">
            {(allFixes ? review.fixes : review.fixes.slice(0, FIXES_SHOWN)).map((f) => (
              <FixRow key={f.id} e={f} data={data} onDone={after} onError={setError} onOpen={() => onOpenExpense(f.id)} />
            ))}
          </ul>
          {review.fixes.length > FIXES_SHOWN && (
            <button type="button" onClick={() => setAllFixes((v) => !v)} className="mt-2 px-1 text-xs font-semibold text-birdie-green">
              {allFixes ? "Show fewer" : `Show all ${review.fixes.length}`}
            </button>
          )}
        </Section>
      )}

      {review.log.length > 0 && (
        <div className="bg-white border border-birdie-green/15 rounded-2xl">
          <button
            type="button"
            onClick={() => setShowLog((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <span className="text-sm font-semibold text-birdie-charcoal">Recent activity</span>
            <span className="text-xs text-birdie-charcoal/50">
              {review.log.length} · {showLog ? "Hide" : "Show"}
            </span>
          </button>
          {showLog && (
            <ul className="divide-y divide-birdie-green/10 border-t border-birdie-green/10">
              {review.log.map((i) => (
                <LogRow key={i.id} i={i} onUndo={() => undo(i.id)} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, sub, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-2 px-1 mb-1">
        <h2 className="font-accent text-base text-birdie-charcoal">{title}</h2>
        <span className="text-xs text-birdie-charcoal/45">{count}</span>
      </div>
      {sub && <p className="text-xs text-birdie-charcoal/55 px-1 mb-2">{sub}</p>}
      {children}
    </section>
  );
}

function AccountChips({ accounts, value, onPick }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]">
      {accounts
        .filter((a) => !a.archived)
        .map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onPick(a.id)}
            className={`shrink-0 min-h-0 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
              Number(value) === a.id ? "bg-birdie-green text-white border-birdie-green" : "bg-white border-birdie-green/20 text-birdie-charcoal/75"
            }`}
          >
            {a.kind === "card" ? "💳" : "🏦"} {a.name}
            {a.owner !== "company" ? ` (${a.owner})` : ""}
          </button>
        ))}
    </div>
  );
}

function QuestionCard({ item, data, onDone, onError }) {
  const [acct, setAcct] = useState(item.account_id || "");
  const [busy, setBusy] = useState(false);
  const [needCard, setNeedCard] = useState(false);
  const moneyIn = item.direction === "in" && item.kind !== "refund";
  const needsAccount = item.needs.includes("account");

  async function send(answer) {
    if (answer.action === "add" && !moneyIn && needsAccount && !acct) {
      setNeedCard(true);
      return;
    }
    setBusy(true);
    try {
      const r = await answerItem(item.id, { ...answer, ...(acct ? { account_id: acct } : {}) });
      onDone(r);
    } catch (e) {
      onError(e.message || "Couldn't save that.");
      setBusy(false);
    }
  }

  const choose = (opt) => {
    if (opt === PERSONAL) return send({ action: "skip", reason: "Personal" });
    if (opt === CUSTOMER) return send({ action: "skip", reason: "Customer payment" });
    if (opt === OTHER_IN) return send({ action: "skip", reason: "Not partner money" });
    if (data.partners.includes(opt)) return send({ action: "add", partner: opt });
    return send({ action: "add", category: opt });
  };

  const options = item.options?.length
    ? item.options
    : moneyIn
      ? [...data.partners, CUSTOMER, OTHER_IN]
      : [];

  return (
    <div className={`bg-white border border-birdie-green/15 rounded-2xl p-3.5 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 rounded-full bg-birdie-cream flex items-center justify-center" aria-hidden>
          {moneyIn ? "💵" : item.source_kind === "amazon" ? "📦" : categoryIcon(item.category)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-sm font-semibold text-birdie-charcoal">{item.description}</span>
            <span className={`shrink-0 text-sm font-semibold tabular-nums ${moneyIn || item.amount_cents < 0 ? "text-birdie-green" : ""}`}>
              {moneyIn ? `+${formatMoney(item.amount_cents)}` : money(item.amount_cents)}
            </span>
          </div>
          <div className="text-[11px] text-birdie-charcoal/50">
            {[day(item.occurred_on), item.source_kind === "amazon" ? "Amazon order" : item.account_name, item.filename]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {item.details && <div className="text-[11px] text-birdie-charcoal/55 mt-0.5">{item.details}</div>}
        </div>
      </div>

      <div className="mt-2.5 rounded-xl bg-birdie-cream/70 px-3 py-2 text-sm text-birdie-charcoal">
        <span aria-hidden>🤖 </span>
        {item.question ||
          (needsAccount ? "Which card or account paid for this?" : moneyIn ? "Who put this money in?" : "Add this?")}
      </div>

      {needsAccount && !moneyIn && (
        <div className="mt-2">
          <div className={`text-[11px] mb-1 ${needCard ? "text-amber-700 font-semibold" : "text-birdie-charcoal/55"}`}>
            {needCard ? "Pick the card first:" : "Paid with:"}
          </div>
          <AccountChips accounts={data.accounts} value={acct} onPick={(id) => {
              setAcct(id);
              setNeedCard(false);
            }} />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => choose(o)}
            className={`min-h-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${
              o === PERSONAL || o === CUSTOMER || o === OTHER_IN
                ? "bg-white border-birdie-charcoal/15 text-birdie-charcoal/65"
                : "bg-birdie-green text-white border-birdie-green"
            }`}
          >
            {data.categories.includes(o) ? `${categoryIcon(o)} ${o}` : o}
          </button>
        ))}
        {!options.length && (
          <Button size="sm" onClick={() => send({ action: "add" })}>
            Add it
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-2.5 pt-2 border-t border-birdie-green/10">
        {!moneyIn ? (
          <select
            value=""
            onChange={(e) => e.target.value && send({ action: "add", category: e.target.value })}
            className="min-w-0 bg-transparent text-xs text-birdie-charcoal/60 outline-none"
            aria-label="Something else"
          >
            <option value="">Something else…</option>
            {data.categories.map((c) => (
              <option key={c} value={c}>
                {categoryIcon(c)} {c}
              </option>
            ))}
          </select>
        ) : (
          <span />
        )}
        <button type="button" onClick={() => send({ action: "skip", reason: "Skipped" })} className="text-xs text-birdie-charcoal/50">
          Skip
        </button>
      </div>
    </div>
  );
}

function ReadyList({ items, data, onDone, onError }) {
  const [off, setOff] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const chosen = items.filter((i) => !off.has(i.id));
  const toggle = (id) =>
    setOff((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function setCategory(i, category) {
    try {
      onDone(await answerItem(i.id, { category }));
    } catch (e) {
      onError(e.message || "Couldn't change it.");
    }
  }

  async function addAll() {
    setBusy(true);
    try {
      onDone(await addItems(chosen.map((i) => i.id)));
    } catch (e) {
      onError(e.message || "Couldn't add them.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Ready to add" count={items.length} sub="The AI is sure about these. Change a category if it's wrong, then add them all.">
      <div className="bg-white border border-birdie-green/15 rounded-2xl overflow-hidden">
        <ul className="divide-y divide-birdie-green/10">
          {items.map((i) => {
            const moneyIn = i.direction === "in" && i.kind !== "refund";
            return (
              <li key={i.id} className="flex items-center gap-3 px-3.5 py-2.5">
                <input
                  type="checkbox"
                  checked={!off.has(i.id)}
                  onChange={() => toggle(i.id)}
                  className="h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
                  aria-label={`Add ${i.description}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-birdie-charcoal truncate">{i.description}</span>
                  <span className="flex items-center gap-1 text-[11px] text-birdie-charcoal/50">
                    <span className="shrink-0">{day(i.occurred_on)} ·</span>
                    {moneyIn ? (
                      <span className="truncate">from {i.partner}</span>
                    ) : (
                      <select
                        value={i.category}
                        onChange={(e) => setCategory(i, e.target.value)}
                        className="min-w-0 bg-transparent outline-none truncate"
                        aria-label="Category"
                      >
                        {data.categories.map((c) => (
                          <option key={c} value={c}>
                            {categoryIcon(c)} {c}
                          </option>
                        ))}
                      </select>
                    )}
                  </span>
                </span>
                <span className={`shrink-0 text-sm font-semibold tabular-nums ${moneyIn || i.amount_cents < 0 ? "text-birdie-green" : ""}`}>
                  {moneyIn ? `+${formatMoney(i.amount_cents)}` : money(i.amount_cents)}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="p-3 border-t border-birdie-green/10">
          <Button onClick={addAll} disabled={busy || !chosen.length} className="w-full">
            {busy ? "Adding…" : `Add ${chosen.length} item${chosen.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </Section>
  );
}

const ISSUE = { date: "No date", account: "Which card?", category: "Category", receipt: "No receipt" };

function FixRow({ e, data, onDone, onError, onOpen }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  async function patch(change) {
    setBusy(true);
    try {
      await patchExpense(e.id, change);
      onDone();
    } catch (err) {
      onError(err.message || "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  }

  async function attach(ev) {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const up = await uploadExpenseFile(await shrinkImage(f));
      await patchExpense(e.id, { file_ids: [up.id] });
      onDone();
    } catch (err) {
      onError(err.message || "Couldn't attach it.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={`bg-white border border-birdie-green/15 rounded-2xl p-3.5 ${busy ? "opacity-50" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onOpen} className="min-w-0 text-left">
          <span className="block text-sm font-semibold text-birdie-charcoal truncate">
            {categoryIcon(e.category)} {e.description}
          </span>
          <span className="block text-[11px] text-birdie-charcoal/50">
            {[e.spent_on && day(e.spent_on), e.account_name, e.category].filter(Boolean).join(" · ")}
          </span>
        </button>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{money(e.amount_cents)}</span>
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {e.issues.map((x) => (
          <span key={x} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            {ISSUE[x]}
          </span>
        ))}
      </div>
      <div className="mt-2 space-y-2">
        {e.issues.includes("date") && (
          <label className="flex items-center gap-2 text-xs">
            <span className="w-14 shrink-0 text-birdie-charcoal/55">Date</span>
            <input
              type="date"
              onChange={(ev) => ev.target.value && patch({ spent_on: ev.target.value })}
              className="rounded-lg border border-birdie-green/20 bg-white px-2 py-1 text-xs"
            />
          </label>
        )}
        {e.issues.includes("account") && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-14 shrink-0 text-birdie-charcoal/55">Paid with</span>
            <div className="min-w-0 flex-1">
              <AccountChips accounts={data.accounts} value="" onPick={(id) => patch({ account_id: id })} />
            </div>
          </div>
        )}
        {e.issues.includes("category") && (
          <label className="flex items-center gap-2 text-xs">
            <span className="w-14 shrink-0 text-birdie-charcoal/55">Category</span>
            <select
              value=""
              onChange={(ev) => ev.target.value && patch({ category: ev.target.value })}
              className="rounded-lg border border-birdie-green/20 bg-white px-2 py-1 text-xs"
            >
              <option value="">Pick one…</option>
              {data.categories
                .filter((c) => c !== "Other")
                .map((c) => (
                  <option key={c} value={c}>
                    {categoryIcon(c)} {c}
                  </option>
                ))}
            </select>
          </label>
        )}
        {e.issues.includes("receipt") && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-14 shrink-0 text-birdie-charcoal/55">Receipt</span>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="min-h-0 rounded-full border border-birdie-green/20 px-2.5 py-1 font-semibold text-birdie-green"
            >
              📎 Attach
            </button>
            <button
              type="button"
              onClick={() => patch({ receipt_not_needed: true })}
              className="min-h-0 rounded-full border border-birdie-charcoal/15 px-2.5 py-1 text-birdie-charcoal/60"
            >
              Don't have one
            </button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={attach} />
          </div>
        )}
      </div>
    </li>
  );
}

const LOG_ICON = { added: "✅", skipped: "↷", merged: "🔗", duplicate: "≡" };

function LogRow({ i, onUndo }) {
  const moneyIn = i.direction === "in" && i.kind !== "refund";
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      <span className="w-5 text-center shrink-0" aria-hidden>
        {LOG_ICON[i.status]}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-birdie-charcoal truncate">{i.description}</span>
        <span className="block text-[11px] text-birdie-charcoal/50">
          {i.status === "added" ? (moneyIn ? `Added as money in from ${i.partner}` : `Added · ${i.category}`) : i.reason}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-xs tabular-nums text-birdie-charcoal/70">{money(i.amount_cents)}</span>
        {i.status === "skipped" && (
          <button type="button" onClick={onUndo} className="text-[11px] font-semibold text-birdie-green">
            Undo
          </button>
        )}
      </span>
    </li>
  );
}
